import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import type { CompanyBaseInfo } from "app/pages/sales-intelligence/modules/company-analysis/types/company";
import type {
    SignalEventDto } from "app/pages/sales-intelligence/sub-modules/account-overview/company/types/signals";
import {
    isNewsSignalEvent
} from "app/pages/sales-intelligence/sub-modules/account-overview/company/types/signals";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import type SignalsGroupedByType from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsGroupedByType";
import createNewsSignalsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/news/factory";
import type SignalEventsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/SignalEventsTooltipViewModel";
import { selectAppliedSignalsMemoized } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { isNewsSignal } from "app/pages/sales-intelligence/sub-modules/signals/utils/news/types";

interface IUseNewsSignalsTooltipProps {
    signalEvents?: SignalEventDto[];
    companyInfo?: Readonly<CompanyBaseInfo>;
    signalsDescription: Readonly<SignalsDescription> | null;
    signalsGroupedByType: Readonly<SignalsGroupedByType>;
}

type UseNewsSignalsTooltipModelResponse = (eventsLimit: number) => Readonly<SignalEventsTooltipViewModel>;

export const useNewsSignalsTooltipModel = (props: IUseNewsSignalsTooltipProps): UseNewsSignalsTooltipModelResponse => {
    const { signalEvents = [], companyInfo, signalsDescription, signalsGroupedByType } = props;

    const appliedSignals = useSelector(selectAppliedSignalsMemoized);

    const newsSignalsTypeEvents = useMemo(() => {
        return signalEvents.filter(isNewsSignalEvent);
    }, [signalEvents]);

    const createViewModel = useCallback(
        (eventsLimit: number): Readonly<SignalEventsTooltipViewModel> => {
            const appliedNewsSignals = appliedSignals.filter(isNewsSignal);

            return createNewsSignalsTooltipViewModel({
                eventsLimit,
                preferredSignals: appliedNewsSignals,
                signalEvents: newsSignalsTypeEvents,
                companyInfo,
                signalsGroupedByType,
            });
        },
        [appliedSignals, companyInfo, newsSignalsTypeEvents, signalsDescription, signalsGroupedByType],
    );

    return createViewModel;
};
